import React, { useCallback, useEffect, useState } from 'react';
import { Col, Divider, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { LeftOutlined, PrinterFilled } from '@ant-design/icons';

import checkIcon from '~/assets/icons/check-icon.png';
import Card from '~/components/Card/Card';
import LegacyWrapper from '~/components/Legacy/LegacyWrapper';
import { Link, Text, Title } from '~/components/Typo/Typo';
import { FontWeight } from '~/theme/constants';
import LanguageSelect from '~/components/forms/LanguageSelect/LanguageSelect';
import Image from '~/components/Image/Image';
import Button from '~/components/Button/Button';
import { GetTransactionResponse } from '~/entities/responses/getTransactionResponse';
import { formatNumber } from '~/utils/number';
import { DATE_FORMAT, formatDate } from '~/utils/date';
import { GetPaymentResponse } from '~/entities/responses/getPaymentResponse';
import { PaymentService } from '~/services/PaymentService';
import Loader from '~/components/Loader/Loader';
import { CryptoService } from '~/services/CryptoService';

import './SuccessPage.scss';

const SuccessPage: React.FC = () => {
  const { t, i18n } = useTranslation();

  const [transactionResponse, setTransactionResponse] = useState<GetTransactionResponse>();
  const [transaction, setTransaction] = useState<GetPaymentResponse>();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingView, setLoadingView] = useState<boolean>(true);

  const handleGetTransaction = useCallback(() => {
    // setLoadingView(true);
    const crypto = new CryptoService();
    crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptData = crypto.decryptData(
      localStorage.getItem('transactionDetails') as string,
    ) as GetTransactionResponse;

    // const getTrans = JSON.parse(localStorage.getItem('transactionDetails') as string);
    const getTrans = decryptData;
    setTransactionResponse(getTrans);
    if (getTrans !== null) {
      setLoadingView(false);
    }
  }, []);

  const handleGetTransactionsInfos = useCallback(() => {
    const crypto = new CryptoService();
    crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
    const decryptData = crypto.decryptData(localStorage.getItem('data') as string) as GetPaymentResponse;

    const getData = decryptData;
    setTransaction(getData as GetPaymentResponse);
  }, []);

  const handleRedirect = useCallback((url: string) => {
    window.location.replace(url);
    // clearData();
  }, []);

  // const clearData = useCallback(() => {
  //   localStorage.removeItem('data');
  //   localStorage.removeItem('transactionDetails');
  // }, []);

  const handleDownloadReceipt = useCallback(async (transactionRef: string) => {
    setLoading(true);
    try {
      const res = await PaymentService.getTransactionReceipt(transactionRef);
      setLoading(false);
      if (res !== '') {
        window.open(res, '_blank');
      }
    } catch (error: any) {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    handleGetTransaction();
    handleGetTransactionsInfos();
    // window.addEventListener('beforeunload', clearData);
    // return () => {
    //   window.removeEventListener('beforeunload', clearData);
    // };
  }, []);
  return (
    <div className="success-page">
      {loadingView ? (
        <Row justify="center">
          <Loader />
        </Row>
      ) : (
        <Row justify="center">
          <Col lg={8} sm={24} md={12} xs={24}>
            <Card slightlyRounded className="successPage-body-content">
              <div className="header">
                <Row justify="space-between">
                  <div className="left-side-header">
                    <div className="merchant-wrapper">
                      <Title className="merchant-label" level={2} weight={FontWeight.LIGHT}>
                        {transactionResponse?.data?.application?.merchant?.name}
                      </Title>
                    </div>
                    <div className="amount-wrapper">
                      <Title className="amount-label" level={4} weight={FontWeight.SEMIBOLD}>
                        {`${formatNumber(
                          transactionResponse?.data?.transaction?.total_amount ?? 0,
                          i18n.language ?? 'fr',
                        )} ${transactionResponse?.data?.transaction?.currency}`}
                      </Title>
                    </div>
                  </div>
                  <div className="right-side-header">
                    <LanguageSelect className="language-success-page" />
                  </div>
                </Row>
              </div>
              <Divider />
              <div className="content">
                <Row justify="center">
                  <Image className="success-icon" image={checkIcon} />
                </Row>

                <p className="tac">{t('successPage:yourPaymentHasbeenApproved')}</p>
                <Card className="transaction-details-wrapper">
                  <div>
                    <Text weight={FontWeight.BOLD}>{t('successPage:merchant')}: </Text>
                    <Text>{transactionResponse?.data?.application?.merchant?.name}</Text>
                  </div>
                  <div>
                    <Text weight={FontWeight.BOLD}>{t('successPage:amountLocalCurrency')}: </Text>
                    <Text>
                      {`${formatNumber(
                        transactionResponse?.data?.transaction?.local_total_amount ?? 0,
                        i18n.language ?? 'fr',
                      )} ${transactionResponse?.data?.transaction?.local_currency}`}
                    </Text>
                  </div>
                  <div>
                    <Text weight={FontWeight.BOLD}>{t('successPage:descriptionOfPurchase')}: </Text>
                    <Text>{transactionResponse?.data?.transaction?.description}</Text>
                  </div>
                  <div>
                    <Text weight={FontWeight.BOLD}>{t('successPage:operatorId')}: </Text>
                    <Text>{transactionResponse?.data?.transaction.operator_id ?? ''}</Text>
                  </div>
                  <div>
                    <Text weight={FontWeight.BOLD}>{t('successPage:transactionId')}: </Text>
                    <Text>{transactionResponse?.data?.transaction.transaction_id}</Text>
                  </div>
                  <div>
                    <Text weight={FontWeight.BOLD}>{t('successPage:paymentDate')}: </Text>
                    <Text>{`${formatDate(
                      transactionResponse?.data?.transaction.payment_date as Date,
                      DATE_FORMAT.DATE_TIME,
                    )} (${transactionResponse?.data?.transaction.payment_date_offset ?? ''})`}</Text>
                  </div>
                </Card>
                <Row justify="center">
                  <Link className="back-button" onClick={() => handleRedirect(transaction?.data?.returnUrl ?? '')}>
                    <LeftOutlined className="back-icon" />
                    <Text>{t('successPage:backToTheSite')}:</Text>
                  </Link>
                </Row>
                <Button
                  onClick={() => handleDownloadReceipt(transaction?.data.transactionRef as string)}
                  icon={<PrinterFilled className="mr-2" />}
                  className="download-button"
                  title={t('successPage:downloadMyReceipt')}
                  size="block"
                  loading={loading}
                  rounded
                />
              </div>
            </Card>
          </Col>
        </Row>
      )}
      <div className="success-page-footer">
        <Row justify="center">
          <LegacyWrapper label={t('home:footer:securedBy')} />
        </Row>
      </div>
    </div>
  );
};

export default SuccessPage;
